// services/api.js - UPDATED with new domain
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.lerenmotiveren.nl';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('ilearn_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Enhanced fetch with timeout and retry logic
const fetchWithTimeout = async (url, options = {}, timeout = 120000) => { // 2 minute default timeout
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - operation took too long');
    }
    throw error;
  }
};

// Helper function to handle API responses with better error messages
const handleResponse = async (response, operation = 'operation') => {
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('ilearn_token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }
    
    let errorMessage;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || `${operation} failed`;
    } catch (e) {
      errorMessage = `${operation} failed with status ${response.status}`;
    }
    
    throw new Error(errorMessage);
  }
  
  return response.json();
};

// Sleep utility for retry delays
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Check evaluation status using the proper status endpoint
const checkEvaluationStatus = async (conversationId) => {
  try {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/conversations/status/${conversationId}`,
      {
        headers: getAuthHeaders()
      },
      10000 // Short timeout for status check
    );
    
    const data = await handleResponse(response, 'status check');
    
    if (data.success && data.hasEvaluation && data.evaluation) {
      return {
        hasEvaluation: true,
        evaluation: data.evaluation
      };
    }
    
    return { hasEvaluation: false };
  } catch (error) {
    console.warn('Could not check evaluation status:', error.message);
    return { hasEvaluation: false };
  }
};

// Auth API
export const authAPI = {
  login: async (credentials) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/auth/login`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      },
      30000 // 30 second timeout for login
    );
    return handleResponse(response, 'login');
  },

  verify: async () => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/auth/verify`,
      {
        headers: getAuthHeaders()
      },
      15000 // 15 second timeout for verification
    );
    return handleResponse(response, 'token verification');
  }
};

// Lessons API
export const lessonsAPI = {
  getProgress: async (lessonId = 'les1') => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/lessons/progress/${lessonId}`,
      {
        headers: getAuthHeaders()
      },
      30000
    );
    return handleResponse(response, 'progress fetch');
  },

  completeModule: async (moduleId, completionData, lessonId = 'les1') => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/lessons/modules/${moduleId}/complete`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ lessonId, completionData })
      },
      60000
    );
    return handleResponse(response, 'module completion');
  },

  // Submit evaluation for a module (used for module6 flow)
  submitEvaluation: async (moduleId, evaluationData, lessonId = 'les1') => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/lessons/modules/${moduleId}/evaluate`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ lessonId, evaluationData })
      },
      60000
    );
    return handleResponse(response, 'evaluation submission');
  },

  // Update newsletter preference tied to evaluation record
  updateNewsletterPreference: async (moduleId, wantsUpdates, lessonId = 'les1') => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/lessons/modules/${moduleId}/newsletter`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ lessonId, wantsUpdates })
      },
      30000
    );
    return handleResponse(response, 'newsletter preference update');
  }
};

// Conversations API with proper parameter order
export const conversationsAPI = {
  start: async (moduleId, conversationNumber, lessonId = 'les1') => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/conversations/start`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ moduleId, conversationNumber, lessonId })
      },
      30000
    );
    return handleResponse(response, 'conversation start');
  },

  sendMessage: async (conversationId, userMessage) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/conversations/message`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversationId, userMessage })
      },
      45000 // 45 seconds for message generation
    );
    return handleResponse(response, 'message send');
  },

  // Enhanced evaluation with timeout handling and retry logic
  evaluate: async (conversationId, conversationHistory, moduleId, persona, onProgress = null) => {
    const maxRetries = 2;
    const baseTimeout = 120000; // 2 minutes base timeout
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (onProgress) {
          onProgress({
            stage: 'starting',
            message: attempt === 1 ? 'Evaluatie starten...' : `Herpoging ${attempt}...`,
            attempt
          });
        }

        const timeout = baseTimeout + (attempt - 1) * 30000; // Increase timeout on retries
        
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/api/conversations/evaluate`,
          {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ conversationId, conversationHistory, moduleId, persona })
          },
          timeout
        );

        if (onProgress) {
          onProgress({
            stage: 'processing',
            message: 'Antwoord verwerken...',
            attempt
          });
        }

        const data = await handleResponse(response, 'evaluation');
        
        if (onProgress) {
          onProgress({
            stage: 'completed',
            message: 'Evaluatie voltooid!',
            attempt
          });
        }

        return data;

      } catch (error) {
        console.error(`Evaluation attempt ${attempt} failed:`, error.message);
        
        // If this was a timeout, check if evaluation was actually saved
        if (error.message.includes('timeout') || error.message.includes('fetch')) {
          if (onProgress) {
            onProgress({
              stage: 'checking',
              message: 'Controleren of evaluatie is voltooid...',
              attempt
            });
          }

          // Wait a moment for backend to potentially finish
          await sleep(5000);
          
          const statusCheck = await checkEvaluationStatus(conversationId);
          if (statusCheck.hasEvaluation) {
            if (onProgress) {
              onProgress({
                stage: 'recovered',
                message: 'Evaluatie gevonden - succesvol hersteld!',
                attempt
              });
            }
            
            // Return the saved evaluation
            return {
              success: true,
              evaluation: statusCheck.evaluation,
              recovered: true
            };
          }
        }

        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          // Check one more time if evaluation was saved
          const finalCheck = await checkEvaluationStatus(conversationId);
          if (finalCheck.hasEvaluation) {
            return {
              success: true,
              evaluation: finalCheck.evaluation,
              recovered: true
            };
          }
          
          throw new Error(`Evaluatie gefaald na ${maxRetries} pogingen: ${error.message}`);
        }

        // Wait before retry
        if (onProgress) {
          onProgress({
            stage: 'retrying',
            message: `Poging ${attempt} gefaald, opnieuw proberen in 3 seconden...`,
            attempt,
            error: error.message
          });
        }
        
        await sleep(3000);
      }
    }
  },

  getHistory: async (conversationId) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/conversations/${conversationId}`,
      {
        headers: getAuthHeaders()
      },
      30000
    );
    return handleResponse(response, 'conversation history');
  }
};

// Contact API (public, no auth required)
export const contactAPI = {
  submit: async (formData) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/contact`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      },
      30000
    );
    return handleResponse(response, 'contact form submission');
  }
};

// General API helper with timeout support
export const api = {
  get: async (endpoint, timeout = 30000) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}${endpoint}`,
      {
        headers: getAuthHeaders()
      },
      timeout
    );
    return handleResponse(response, 'GET request');
  },

  post: async (endpoint, data, timeout = 60000) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}${endpoint}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      },
      timeout
    );
    return handleResponse(response, 'POST request');
  }
};

export default {
  auth: authAPI,
  lessons: lessonsAPI,
  conversations: conversationsAPI,
  contact: contactAPI,
  ...api
};